import { ComponentProps, useState } from "react";
import Ad from "@components/Advertisement/Ad";
import ListingPageNewsletter from "@features/newsletter/ListingPageNewsletter";
import { useSetPageParam, useSetPageParamProps } from "@hooks/useSetPageParam";
import { useWindowSize } from "@hooks/useWindowSize";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type TemplateGCardProps = {
  /** This is the classname of the component. */
  className?: string;
  /** The card prop to be displayed. */
  cardProp: ComponentProps<typeof Card>;
  /** The index of the card to be displayed. */
  index: number;
  /** Optional Card variant to override default styles */
  cardVariantOverride?: CardVariations;
} & Pick<useSetPageParamProps, "lang">;

/** This is the template G card component. */
const TemplateGCard = ({
  className,
  cardProp,
  index,
  cardVariantOverride,
  lang = "en",
}: TemplateGCardProps): React.ReactElement => {
  const [adsHeight, setAdsHeight] = useState<number>(0);
  const isHideAd = adsHeight < 5;

  const { ref } = useSetPageParam({ cardIndex: index, lang });
  const { isScreenLG, isScreenMD } = useWindowSize();

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      width: "w-3/10 md:w-1/4",
      position: isScreenMD ? "right" : "left",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-7/10 md:w-3/4",
      extraClass: isScreenMD ? "pr-4" : "pl-4",
      blurb: { extraClass: "text-gray-850", wrapperClassName: cn("lg:my-2") },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2 lg:mt-auto"),
    },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
  };

  return (
    <>
      {index === 3 && !isScreenLG ? (
        <ListingPageNewsletter
          className={cn(
            "relative py-4",
            "after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175"
          )}
        />
      ) : null}

      {index === 4 ? (
        <Ad
          adType="bn1"
          className={cn(
            "relative flex flex-col items-center justify-center py-3",
            "after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175",
            { "min-h-0 border-y-0 bg-none py-0 after:hidden": isHideAd }
          )}
          setAdsHeight={setAdsHeight}
        />
      ) : null}

      <Card
        {...cardProp}
        variations={cardVariantOverride || variant}
        className={cn(
          "relative lg:items-stretch",
          "after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175",
          className
        )}
        ref={ref}
      />
    </>
  );
};

export default TemplateGCard;
